/**
 * Liga/desliga campanha, conjunto ou anúncio.
 *
 * Desligar é imediato: pausar nunca custa dinheiro. Ligar abre confirmação
 * mostrando a verba diária do objeto contra o teto do workspace — é o momento
 * em que a verba começa a sair, e a pessoa precisa ver o número antes.
 *
 * Só renderiza para quem tem `ads:activate`; o chamador mostra só o selo de
 * status para os demais. Estados em que a Meta não deixa alternar (arquivado,
 * em processamento, em análise) ficam desabilitados com o motivo no tooltip.
 */

import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { moeda } from "@/lib/meta-ads-format";
import { Loader2 } from "lucide-react";

export type ObjetoTipo = "campaign" | "adset" | "ad";

const ROTULO: Record<ObjetoTipo, string> = { campaign: "a campanha", adset: "o conjunto", ad: "o anúncio" };

const NAO_ALTERNAVEL: Record<string, string> = {
  ARCHIVED: "Objeto arquivado — reative pelo Gerenciador de Anúncios.",
  IN_PROCESS: "A Meta ainda está processando este objeto.",
  PENDING_REVIEW: "Em análise pela Meta.",
  DISAPPROVED: "Reprovado pela Meta — corrija no Gerenciador.",
  DELETED: "Excluído.",
};

export function StatusToggle({
  objectId,
  objectType,
  status,
  effectiveStatus,
  dailyBudgetCents,
  currency,
  onChanged,
}: {
  objectId: string;
  objectType: ObjetoTipo;
  /** Status CONFIGURADO (ACTIVE/PAUSED) — é o que o switch representa. */
  status: string | null | undefined;
  effectiveStatus: string | null | undefined;
  dailyBudgetCents: number | null | undefined;
  currency: string | null;
  onChanged: () => void;
}) {
  const [confirmando, setConfirmando] = useState(false);
  const limits = trpc.metaAds.limits.get.useQuery(undefined, { staleTime: 5 * 60_000, enabled: confirmando });

  const mutation = trpc.metaAds.status.set.useMutation({
    onSuccess: (r) => {
      toast.success(
        r.status === "ACTIVE"
          ? "No ar. A Meta pode levar alguns segundos para refletir."
          : "Pausado. A Meta pode levar alguns segundos para refletir.",
      );
      onChanged();
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  const ligado = status === "ACTIVE";
  const motivo = NAO_ALTERNAVEL[effectiveStatus ?? ""];
  const cap = limits.data?.budgetCapCents ?? null;
  const acimaDoTeto = cap != null && dailyBudgetCents != null && dailyBudgetCents > cap;

  const alternar = (v: boolean) => {
    if (v) setConfirmando(true);
    else mutation.mutate({ objectId, objectType, status: "PAUSED" });
  };

  return (
    <>
      <span title={motivo ?? (ligado ? "Pausar" : "Ativar")} className="inline-flex items-center gap-1">
        {mutation.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <Switch checked={ligado} onCheckedChange={alternar} disabled={!!motivo || mutation.isPending} aria-label={ligado ? "Pausar" : "Ativar"} />
        )}
      </span>

      <AlertDialog open={confirmando} onOpenChange={setConfirmando}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Colocar {ROTULO[objectType]} no ar?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>A partir de agora a Meta começa a cobrar pela veiculação.</p>
                <div className="rounded border p-3">
                  <p>
                    Verba diária: <strong>{dailyBudgetCents != null ? moeda(dailyBudgetCents, currency) : "definida no conjunto ou na campanha"}</strong>
                  </p>
                  <p className="text-muted-foreground">
                    Limite do workspace: {cap != null ? `${moeda(cap, currency)}/dia` : "sem limite definido"}
                  </p>
                  {acimaDoTeto && (
                    <p className="mt-1 font-medium text-red-600">
                      Acima do limite — o sistema vai recusar. Reduza a verba ou peça ao admin para ajustar o limite.
                    </p>
                  )}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={acimaDoTeto}
              onClick={() => {
                setConfirmando(false);
                mutation.mutate({ objectId, objectType, status: "ACTIVE" });
              }}
            >
              Ativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
